<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { useField, useForm } from 'vee-validate'
import { object, array, date, string } from 'yup'
import { useI18n } from 'vue-i18n'
import {
  DCard,
  DBtn,
  DSelectFieldSearch,
  DMultiselect,
  DModal,
  DLoading
} from 'front-library'
import {
  getPayroll,
  getConcepts,
  getReportRequest,
  getListYear,
  getProcessCountry,
  convertArrayToString
} from '@/app/core/services/reports'

import {
  ReportISRHonduras,
  generateISRHonduras
} from '@/app/core/services/reports/banks'
usePayrollChange(() => back())

/* store, router, notifications */
const storePayroll = usePayrollStore()
const store = useUserStore()
const router = useRouter()
const route = useRoute()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
    ))
})

/* Datos */
const itemsPayrolls = ref<any[]>([])
const itemsProcesses = ref<string[]>([])
const itemsConcepts = ref<string[]>([])
const allConcepts = ref<any[]>([])
const allIdsConceptsSelected = ref<any[]>([])
const itemsYears = ref<any[]>([])
const itemsIdentificationFormat = ref([
  { id: 'CEDULA', name: 'Cedula' },
  { id: 'FISCAL-ID', name: 'Fiscal ID' }
])
const searchIdentificationFormat = ref<string>('')
const itemsMonths = ref([
  { id: '1', name: t('reports.months.january') },
  { id: '2', name: t('reports.months.february') },
  { id: '3', name: t('reports.months.march') },
  { id: '4', name: t('reports.months.april') },
  { id: '5', name: t('reports.months.may') },
  { id: '6', name: t('reports.months.june') },
  { id: '7', name: t('reports.months.july') },
  { id: '8', name: t('reports.months.august') },
  { id: '9', name: t('reports.months.september') },
  { id: '10', name: t('reports.months.october') },
  { id: '11', name: t('reports.months.november') },
  { id: '12', name: t('reports.months.december') }
])
const searchMonths = ref<string>('')
const urlFile = ref(null)
const refresh = ref()

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  payrolls: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  processes: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  retentionConcepts: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  incomeTaxConcepts: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  period: string().required(t('form.valid.required')),
  year: string().required(t('form.valid.required')),
  identificationFormat: string().required(t('form.valid.required'))
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<any /*paramentISRHonduras*/>({
  initialValues: {},
  validationSchema
})

/* Definicion campos del formulario */
const { value: payrolls } = useField<string[]>(ref('payrolls'))
const { value: processes } = useField<string[]>(ref('processes'))
const { value: retentionConcepts } = useField<string[]>(
  ref('retentionConcepts')
)
const { value: incomeTaxConcepts } = useField<string[]>(
  ref('incomeTaxConcepts')
)
const { value: period } = useField<string>('period')
const { value: year } = useField<string>('year')
const { value: identificationFormat } = useField<string>(
  ref('identificationFormat')
)

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: any) => {
  values.retentionConcepts = filterCodeConcepts(retentionConcepts.value)
  values.incomeTaxConcepts = filterCodeConcepts(incomeTaxConcepts.value)

  const path = route.params.code as string
  const decodeCode = atob(path)
  const createReport: ReportISRHonduras = {
    code: decodeCode,
    parameters: values
  }
  void generateReport(createReport)
})

/* Crear solicitud del reporte */
const generateReport = async (values: any) => {
  loading.value = true
  messageLoading.value = t('loading.report')
  try {
    const response = await generateISRHonduras(values)
    reportRequest(response)
  } catch (error: any) {
    showError(error)
  }
}

/* Mostrar estado del la solicitud del reporte generado */
const reportRequest = (id: string) => {
  urlFile.value = null

  refresh.value = setInterval(async () => {
    try {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        if (response.errors.length === 1) {
          descriptionModal.value = response.errors[0]
        } else {
          for (let i = 0; i < response.errors.length; i++) {
            const error = response.errors[i]
            descriptionModal.value += error
            if (i < response.errors.length - 1) {
              descriptionModal.value += '<br>'
            }
          }
        }
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    } catch (error: any) {
      showError(error)
    }
  }, 5000)
}

/* Listar conceptos */
const listConcepts = async () => {
  loading.value = true
  messageLoading.value = t('loading.concepts')
  itemsConcepts.value = []
  const strProcesses = await convertArrayToString(processes.value)
  const strPayrolls = await convertArrayToString(payrolls.value)
  try {
    if (payrolls.value?.length > 0) {
      allConcepts.value = await getConcepts(
        strPayrolls,
        'INS',
        '',
        strProcesses
      )

      itemsConcepts.value = [
        ...new Map(
          allConcepts.value.map((item: any) => [item['code'], item])
        ).values()
      ]

      if (retentionConcepts.value?.length > 0)
        filterCodeConcepts(retentionConcepts.value)
      if (incomeTaxConcepts.value?.length > 0)
        filterCodeConcepts(incomeTaxConcepts.value)

      retentionConcepts.value =
        filterChangeProcesses(retentionConcepts.value) ?? []
      incomeTaxConcepts.value =
        filterChangeProcesses(incomeTaxConcepts.value) ?? []

      if (itemsConcepts.value.length === 0) {
        notifications.addNotification({
          title: t('reports.notification.noConceptsFound'),
          text: t('reports.notification.noConcepts'),
          type: 'warning',
          time: 10000
        })
      }
    }
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

/* Filtrar procesos */
const filterChangeProcesses = (concepts: string[]) => {
  if (concepts) {
    const conceptsSelected = itemsConcepts.value
      .filter((_: any) => concepts.indexOf(_.code) > -1)
      .map((_: any) => _.code)
    return conceptsSelected
  }
}

/* Filtrar codigos de conceptos */
const filterCodeConcepts = (concepts: any) => {
  allIdsConceptsSelected.value = allConcepts.value
  allIdsConceptsSelected.value = allIdsConceptsSelected.value
    .filter(item => concepts.indexOf(item.code) > -1)
    .map((_: any) => _.id)
  return allIdsConceptsSelected.value
}

/* Mostrar notificación de error */
const showError = (error: any) => {
  loading.value = false
  notifications.addNotification({
    title: error.message,
    text: error.response?.data?.message || '',
    type: 'error',
    time: 10000
  })
}

/* Cerrar modal */
const closeModal = () => {
  showModal.value = false
}

/* Volver a la lista de reportes */
const back = () => {
  void router.push({ name: 'MainReports' })
}

onMounted(async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const [promisePayrolls, promiseProcess] = await Promise.all([
      getPayroll(store.user?.id as string),
      getProcessCountry()
    ])

    itemsPayrolls.value = promisePayrolls
    payrolls.value = itemsPayrolls.value
      .filter((_: any) => _.id === storePayroll.payroll?.id)
      .map((_: any) => _.id)

    itemsProcesses.value = promiseProcess
    processes.value = itemsProcesses.value
      .filter((_: any) => _.id === 'N' || _.id === 'E')
      .map((_: any) => _.id)

    identificationFormat.value = 'CEDULA'

    itemsYears.value = getListYear(6)
    listConcepts()

    loading.value = false
  } catch (error: any) {
    showError(error)
  }
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <DModal
      :open="showModal"
      :typeAlert="typeAlertModal"
      :title="titleModal"
      :description="descriptionModal"
      @closeModal="closeModal">
      <template v-slot:description v-if="urlFile != null">
        <a
          class="underline tracking-widest text-blue-600"
          :href="urlFile"
          target="_blank"
          v-html="t('reports.notification.viewFile')" />
      </template>
    </DModal>
    <div class="bg-gray-50 sm:pb-5 lg:pb-12">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('reports.isr')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 10rem)">
      <div class="flex flex-col gap-4">
        <DCard :footer="true">
          <div class="grid grid-cols-4">
            <div class="col-span-4 sm:pb-5 lg:pb-0 px-3">
              <div class="grid grid-cols-6 gap-4">
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.payrolls')"
                    v-model="payrolls"
                    :headerTitle="t('reports.fields.payrolls')"
                    :required="true"
                    :options="itemsPayrolls"
                    :error="errors.payrolls"
                    @change="listConcepts" />
                </div>

                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.processes')"
                    v-model="processes"
                    :header-title="t('reports.fields.processes')"
                    :required="true"
                    :options="itemsProcesses"
                    :error="errors.processes"
                    @change="listConcepts()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.retentionBase')"
                    :header-title="t('reports.fields.retentionBase')"
                    v-model="retentionConcepts"
                    :value-key="'code'"
                    :options="itemsConcepts"
                    :required="true"
                    :error="errors.retentionConcepts" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.incomeTax')"
                    :header-title="t('reports.fields.incomeTax')"
                    v-model="incomeTaxConcepts"
                    :value-key="'code'"
                    :options="itemsConcepts"
                    :required="true"
                    :error="errors.incomeTaxConcepts" />
                </div>

                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.period')"
                    v-model="period"
                    :required="true"
                    :options="filterOptions(itemsMonths, searchMonths)"
                    :error="errors.period"
                    @filter="searchMonths = $event" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.year')"
                    v-model="year"
                    :required="true"
                    :options="itemsYears"
                    :error="errors.year" />
                </div>

                <hr class="col-span-6" />
                <h1
                  class="col-span-6 text-gray-600"
                  v-html="t('reports.tabs.modifiers')" />
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.identificationFormat')"
                    v-model="identificationFormat"
                    :required="true"
                    :options="
                      filterOptions(
                        itemsIdentificationFormat,
                        searchIdentificationFormat
                      )
                    "
                    :error="errors['modifiers.identificationFormat']"
                    @filter="searchIdentificationFormat = $event" />
                </div>
              </div>
            </div>
          </div>
          <template v-slot:footer>
            <div class="flex justify-end gap-2">
              <DBtn
                type="button"
                color="secondary"
                @click="back"
                class="px-5 py-2 font-semibold"
                v-html="t('form.button.back')" />
              <DBtn
                color="excel"
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit()"
                v-html="t('form.button.excel')" />
            </div>
          </template>
        </DCard>
      </div>
    </form>
  </div>
</template>
